import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';

interface Props {
  page: number;              // 1-based
  pageSize: number;
  total: number;
  onPageChange: (p: number) => void;
  pageSizeOptions?: number[];
  onPageSizeChange?: (n: number) => void;
}

// Page numbers with ellipses: 1 … 4 5 [6] 7 8 … 20
function pageList(page: number, pages: number): Array<number | '…'> {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  const out: Array<number | '…'> = [1];
  const lo = Math.max(2, page - 1);
  const hi = Math.min(pages - 1, page + 1);
  if (lo > 2) out.push('…');
  for (let p = lo; p <= hi; p++) out.push(p);
  if (hi < pages - 1) out.push('…');
  out.push(pages);
  return out;
}

export function Pagination({ page, pageSize, total, onPageChange, pageSizeOptions, onPageSizeChange }: Props) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);

  const btn = 'h-8 min-w-8 px-2 rounded-md text-xs font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed';

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-4 text-sm">
      <div className="flex items-center gap-3 text-slate-500 text-xs">
        <span className="tabular-nums">
          Showing <strong className="text-ink">{from}–{to}</strong> of <strong className="text-ink">{total}</strong>
        </span>
        {pageSizeOptions && onPageSizeChange && (
          <label className="flex items-center gap-1.5">
            <span>Per page</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="h-8 px-2 rounded-md border border-slate-300 bg-white text-xs text-ink"
            >
              {pageSizeOptions.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
        )}
      </div>

      {pages > 1 && (
        <nav className="flex items-center gap-1 flex-wrap" aria-label="Pagination">
          <button className={cn(btn, 'text-slate-600 hover:bg-slate-100')} disabled={page <= 1}
            onClick={() => onPageChange(page - 1)} aria-label="Previous page">
            <ChevronLeft className="w-4 h-4" />
          </button>
          {pageList(page, pages).map((p, i) =>
            p === '…' ? (
              <span key={`e${i}`} className="px-1 text-slate-400">…</span>
            ) : (
              <button key={p} onClick={() => onPageChange(p)} aria-current={p === page ? 'page' : undefined}
                className={cn(btn, p === page ? 'bg-ink text-white' : 'text-slate-600 hover:bg-slate-100')}>
                {p}
              </button>
            ),
          )}
          <button className={cn(btn, 'text-slate-600 hover:bg-slate-100')} disabled={page >= pages}
            onClick={() => onPageChange(page + 1)} aria-label="Next page">
            <ChevronRight className="w-4 h-4" />
          </button>
        </nav>
      )}
    </div>
  );
}
